// ============================================================================
// THE HELPER — rung 2 of three. 2026-09-03, Ben.
//
//   FORM   → you know the words. Pick a division, pick a reason. Done.
//   HELPER → you don't. Answer plain questions and the app works out the words.
//   TEXT   → even the questions don't fit. Describe it; Finance classifies it.
//
// ⛔ TEXT IS THE LAST RESORT, NOT THE ESCAPE. Ben, 2026-09-03: *"Form first. If
// they cannot figure out the form, HELPER. If HELPER cannot help, THEN TEXT."*
// A previous pass wired "I'm not sure" straight to a free-text box, which skipped
// this entire rung and sent Finance a paragraph where a classification was
// available for the asking.
//
// ⭐ THE POINT OF THIS FILE: the "What happened?" question IS the division picker,
// asked in plain English. An AM who takes the helper usually comes out FULLY
// CLASSIFIED and never reads the word "Concession". `Unclassified` is what
// happens when they reject all six real answers — the bottom of the ladder, not
// the destination.
//
// ⛔ NO VOCABULARY IS DUPLICATED HERE. This file names `division_id` and
// `reason_code` — STRUCTURAL ids that carry validated FKs, the same standing as
// PROJECT_EVENT_CLASS in use-service-catalog.ts. It never names a division's
// DISPLAY LABEL: those are renamed by the business in `service_divisions` and are
// read from the catalog at render time. Hardcoding labels is exactly how the old
// service constant drifted from 56 variants to 77.
//
// ⚠️ The ids below are a coupling to data, so `assertHelperCodesExist()` re-checks
// them against the live catalog and shouts on drift — the same guard shape as
// `assertHourlyTierMatches` (TCSD-0169). A reason code renamed in the database
// without touching this file is a loud console error, not a silent dead branch.
// ============================================================================

/** projects.event_class for a record nobody has classified yet. */
export const UNCLASSIFIED_EVENT_CLASS = 'Unclassified';

/**
 * One plain-language answer to "What happened?", and the structural ids it means.
 *
 * `followUp` is the second question — two or three plain sentences that pick the
 * reason code. It is never a list of reason codes with their database labels.
 */
export interface WhatHappened {
  key: string;
  /** The sentence the AM reads. Written for someone who has never seen the schema. */
  prompt: string;
  division_id: string;
  followUp: { reason_code: string; prompt: string }[];
}

/**
 * ⛔ ORDER MATTERS AND IS NOT ALPHABETICAL. The two commonest real cases are
 * first — covering a shift and extra work are what the fourteen stranded records
 * were about — and the fall-through is last, after the AM has read six real
 * answers and rejected every one. That ordering IS the friction Ben asked for:
 * *"it forces them to think a bit."*
 */
export const WHAT_HAPPENED: WhatHappened[] = [
  {
    key: 'cover',
    prompt: 'Someone covered a shift that wasn’t theirs',
    division_id: 'COVERAGE',
    followUp: [
      { reason_code: 'SUB_ABSENCE_FILL', prompt: 'Someone was out and another sub filled in' },
      { reason_code: 'STAFFING_GAP', prompt: 'We were short-handed' },
    ],
  },
  {
    key: 'extra',
    prompt: 'We did work beyond the usual scope',
    division_id: 'COVERAGE',
    followUp: [
      { reason_code: 'SPECIAL_REQUEST', prompt: 'The client asked for it' },
      { reason_code: 'EXTRA_DAY', prompt: 'An extra visit on top of the schedule' },
    ],
  },
  {
    key: 'missed',
    prompt: 'We missed a clean, or broke something',
    division_id: 'RECOVERY',
    followUp: [
      { reason_code: 'MISSED_SERVICE', prompt: 'We did not show up, or skipped it' },
      { reason_code: 'DAMAGE_REPAIR', prompt: 'Something was damaged' },
      { reason_code: 'REWORK_OUR_MISS', prompt: 'We had to redo our own work' },
    ],
  },
  {
    key: 'wrong',
    prompt: 'An amount was wrong and needs fixing',
    division_id: 'CORRECTION',
    followUp: [
      { reason_code: 'CLIENT_UNDERBILL', prompt: 'We charged the client too little' },
      { reason_code: 'CLIENT_OVERBILL', prompt: 'We charged the client too much' },
      { reason_code: 'SUB_UNDERPAY', prompt: 'We paid the sub too little' },
      { reason_code: 'SUB_OVERPAY', prompt: 'We paid the sub too much' },
    ],
  },
  {
    key: 'free',
    prompt: 'We chose not to charge for something',
    division_id: 'CONCESSION',
    followUp: [
      { reason_code: 'GOODWILL', prompt: 'A goodwill gesture' },
      { reason_code: 'RETENTION', prompt: 'To keep the account happy' },
      { reason_code: 'ONBOARD_COURTESY', prompt: 'Part of getting them started' },
    ],
  },
  {
    key: 'startup',
    prompt: 'A new building or account is starting up',
    division_id: 'STARTUP',
    followUp: [
      { reason_code: 'FACILITY_STARTUP', prompt: 'A new building' },
      { reason_code: 'ACCOUNT_STARTUP', prompt: 'A whole new customer' },
      { reason_code: 'EQUIPMENT_DEPLOY', prompt: 'Getting equipment in place' },
    ],
  },
];

/** ⛔ The ONLY route to Unclassified, and it is reached from inside the helper. */
export const NONE_OF_THESE = 'none';

export interface HelperAnswers {
  what: string | null;
  reason_code: string | null;
  /** What the AM typed. Required on the fall-through, optional otherwise. */
  words: string;
}

export interface HelperResult {
  /** null while the helper is unfinished. */
  division_id: string | null;
  reason_code: string | null;
  /** null until "What happened?" is answered; the caller maps it via the catalog. */
  event_class_hint: 'classified' | 'unclassified' | null;
  /** true when the record cannot be saved as it stands, and why. */
  blockers: string[];
  done: boolean;
}

/**
 * What the helper has worked out so far.
 *
 * ⛔ It returns `division_id`, never an `event_class` string. The division →
 * class mapping is ONE CASE inside the database (`event_class_for_division`) and
 * reaches the app through `v_event_classes`; restating it here would be a second
 * copy of a rule that already has one home. The fall-through is the single
 * exception, because it has no division to map from.
 */
export function helperResult(a: HelperAnswers): HelperResult {
  const blockers: string[] = [];

  if (!a.what) {
    return { division_id: null, reason_code: null, event_class_hint: null, blockers: ['what happened'], done: false };
  }

  if (a.what === NONE_OF_THESE) {
    // ⛔ The fall-through still will not take an empty record. This mirrors
    // `projects_reason_note_required_off_project`, which REFUSES an off-project
    // row with no words — the helper says so before the database has to.
    if (!a.words.trim()) blockers.push('a description — Finance cannot classify a record that says nothing');
    return {
      division_id: null,
      reason_code: null,
      event_class_hint: 'unclassified',
      blockers,
      done: blockers.length === 0,
    };
  }

  const chosen = WHAT_HAPPENED.find((w) => w.key === a.what) ?? null;
  if (!chosen) {
    // An answer key we do not recognise. Fail LOUD rather than silently
    // classifying the record as something the AM did not pick.
    return { division_id: null, reason_code: null, event_class_hint: null, blockers: ['what happened'], done: false };
  }

  if (!a.reason_code) blockers.push('which one it was');

  return {
    division_id: chosen.division_id,
    reason_code: a.reason_code,
    event_class_hint: 'classified',
    blockers,
    done: blockers.length === 0,
  };
}

/** Every reason code this file can produce. Used by the drift guard and its test. */
export function helperReasonCodes(): string[] {
  return WHAT_HAPPENED.flatMap((w) => w.followUp.map((f) => f.reason_code));
}

/** Every division id this file can produce. */
export function helperDivisionIds(): string[] {
  return Array.from(new Set(WHAT_HAPPENED.map((w) => w.division_id)));
}

/**
 * ⚠️ THE DRIFT GUARD. The ids above are a coupling to `service_divisions` and
 * `special_reasons`; if either is renamed in the database this file goes quietly
 * stale and an AM's answer maps to a code that no longer exists — which the
 * database would refuse at save time with an error nobody can read.
 *
 * Same shape as `assertHourlyTierMatches` (TCSD-0169): it does not throw and it
 * does not block the screen. It shouts, for a reader who can see the catalog.
 * ⛔ Do not turn this into a runtime derivation — the plain-English questions are
 * NOT in the database and cannot be derived from it.
 */
export function assertHelperCodesExist(
  liveDivisionIds: string[],
  liveReasonCodes: string[],
): string[] {
  const problems: string[] = [];
  if (liveDivisionIds.length === 0 || liveReasonCodes.length === 0) return problems;

  for (const id of helperDivisionIds()) {
    if (!liveDivisionIds.includes(id)) problems.push(`division_id '${id}' is not in the live catalog`);
  }
  for (const code of helperReasonCodes()) {
    if (!liveReasonCodes.includes(code)) problems.push(`reason_code '${code}' is not in the live catalog`);
  }
  if (problems.length > 0) {
    console.error(
      '[classify-helper] The helper offers answers the database no longer accepts:\n  ' +
        problems.join('\n  ') +
        '\nAn AM choosing one of these would hit a refusal they cannot read. Fix classify-helper.ts.',
    );
  }
  return problems;
}

// ============================================================================
// THE REST OF THE HELPER — 2026-09-03, second pass.
//
// ⛔ WHAT WAS WRONG WITH THE FIRST ONE. Ben: *"It stops after a couple fields.
// Why were these removed? The demo had them. Asking them helps Jose with the
// classification. As it stands, it is not helpful… You took the three rung
// concept and made a mini (mostly useless) helper that still basically defaults
// to text."*
//
// He is right, and the reason it happened is worth naming: I built the two
// questions that produce a `division_id` and stopped, because those were the two
// the DATABASE needed. The helper does not exist to satisfy the database. It
// exists so that a person who cannot classify a record can still tell Finance
// enough to classify it FOR them — and "enough" is where, when, what, who, and
// whether money moves. A classification with no building and no sub is a
// category with nothing under it.
//
// ⭐ THE RULE FOR "I'M NOT SURE", WHICH IS THE POINT OF THE WHOLE THING.
// Every question takes "I'm not sure" and NONE of them blocks. An unknown does
// two things and only two: it is named for Finance in the summary, and it makes
// the closing note REQUIRED. Ben: *"If 'I don't know' or 'it's complicated' are
// selected, THEN default to text."* Text is what an unknown falls back to —
// per question, at the end — never the first thing offered.
//
// ⛔ AND STILL ONLY ONE ROUTE TO Unclassified: "None of these" on what-happened.
// An AM who does not know the BUILDING but does know they covered a shift is
// classified as Coverage with a note saying the building is unknown. That is a
// useful record. Sending it to the sorting queue instead would throw away the
// one answer they were sure of.
// ============================================================================

/** Every question's "I'm not sure" answer. One token, so one rule can find it. */
export const UNSURE = 'unsure';

/** Q2 — roughly when. A stretch of days is still ONE event. */
export type WhenKind = 'day' | 'range' | typeof UNSURE;

/**
 * Q5 — does money need to move, and which way?
 *
 * ⭐ THIS QUESTION IS AFB-0085 (3), WHICH BEN HAS ASKED FOR THREE TIMES:
 * *"may an AM hand a record to Jose WITHOUT a money answer?"* Yes. `none` and
 * `unsure` are real answers here, and neither invents a figure.
 *
 * ⛔ `none` — "the hours are already logged" — is the answer that did not exist
 * before, and six of the fourteen stranded records are exactly it.
 */
export const MONEY_DIRECTIONS = [
  { key: 'bill', prompt: 'Charge the client', needsClient: true, needsSub: false },
  { key: 'pay', prompt: 'Pay a sub more', needsClient: false, needsSub: true },
  { key: 'claw', prompt: 'Take money back from a sub', needsClient: false, needsSub: true },
  { key: 'both', prompt: 'Both charge and pay', needsClient: true, needsSub: true },
  {
    key: 'none',
    prompt: 'No — the hours are already logged, this just explains why',
    needsClient: false,
    needsSub: false,
  },
  { key: UNSURE, prompt: 'I don’t know yet — let Finance work it out', needsClient: false, needsSub: false },
] as const;

export type MoneyDirection = (typeof MONEY_DIRECTIONS)[number]['key'];

export function moneyDirection(key: string | null) {
  return MONEY_DIRECTIONS.find((m) => m.key === key) ?? null;
}

/** Everything the seven screens collect. */
export interface HelperAnswersFull extends HelperAnswers {
  /** Buildings it happened at. EMPTY + `buildingsUnsure` is a legitimate answer. */
  facility_ids: string[];
  buildingsUnsure: boolean;
  when: WhenKind | null;
  /** ISO dates. One for `day`; start and end for `range`. */
  date_from: string;
  date_to: string;
  money: MoneyDirection | null;
  /** Who did the work. '' + `subUnsure` is a legitimate answer. */
  assigned_partner_id: string;
  subUnsure: boolean;
}

export function blankHelperAnswers(): HelperAnswersFull {
  return {
    what: null,
    reason_code: null,
    words: '',
    facility_ids: [],
    buildingsUnsure: false,
    when: null,
    date_from: '',
    date_to: '',
    money: null,
    assigned_partner_id: '',
    subUnsure: false,
  };
}

/**
 * Which questions the AM answered with "I'm not sure".
 *
 * ⛔ NAMED, NEVER COUNTED. Finance reads "the building and the sub are unknown",
 * not "3 unknowns" — the second is a number nobody can act on.
 */
export function unknownAnswers(a: HelperAnswersFull): string[] {
  const gaps: string[] = [];
  if (a.buildingsUnsure) gaps.push('which building');
  if (a.when === UNSURE) gaps.push('when it happened');
  if (a.subUnsure) gaps.push('who did the work');
  if (a.money === UNSURE) gaps.push('whether money needs to move');
  return gaps;
}

/**
 * ⭐ THE RULE BEN RESTATED. An unknown does not derail the helper and does not
 * open a text box early — it makes the CLOSING note required, so the one thing
 * Finance is left with is the AM's own words about the part they could not
 * answer. "If 'I don't know' … is selected, THEN default to text."
 */
export function notesRequired(a: HelperAnswersFull): boolean {
  return a.what === NONE_OF_THESE || unknownAnswers(a).length > 0;
}

export interface HelperFullResult extends HelperResult {
  /** Named gaps, for the summary and for the note handed to Finance. */
  gaps: string[];
  /** Does this path still owe an amount? Drives whether amounts are asked for. */
  needsClientAmount: boolean;
  needsSubAmount: boolean;
}

/**
 * The whole helper's verdict.
 *
 * ⛔ IT LAYERS ON `helperResult` RATHER THAN RESTATING IT. The classification
 * rule — which answer means which division, and that only the fall-through is
 * Unclassified — has one home and this is not it. What is added here is the
 * three things the first pass dropped: the gaps, the note obligation, and
 * whether an amount is still owed.
 */
export function helperFullResult(a: HelperAnswersFull): HelperFullResult {
  const base = helperResult(a);
  const gaps = unknownAnswers(a);
  const blockers = [...base.blockers];

  if (a.what !== null && a.when === null) blockers.push('roughly when');
  if (a.what !== null && a.money === null) blockers.push('whether money needs to move');

  // ⛔ The note is required by the ANSWERS, not by the screen. A gap with no
  // words is a record Finance cannot act on — which is the state AFB-0085
  // describes, arrived at politely.
  if (notesRequired(a) && !a.words.trim()) {
    blockers.push(
      a.what === NONE_OF_THESE
        ? 'a description — Finance cannot classify a record that says nothing'
        : `a note — you said you were not sure about ${joinPlain(gaps)}`,
    );
  }

  const dir = moneyDirection(a.money);
  return {
    ...base,
    // The fall-through's own blocker is already in `base`; do not print it twice.
    blockers: Array.from(new Set(blockers)),
    gaps,
    needsClientAmount: dir?.needsClient ?? false,
    needsSubAmount: dir?.needsSub ?? false,
    done:
      a.what !== null &&
      a.when !== null &&
      a.money !== null &&
      Array.from(new Set(blockers)).length === 0,
  };
}

/** "a and b" · "a, b and c". Shared by the blocker text and the note. */
export function joinPlain(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * The sentence that goes on the record for Finance.
 *
 * ⛔ EVERYTHING THE AM ANSWERED, INCLUDING THE MONEY DIRECTION, IN PROSE. There
 * is no column for "which way does money move" and this run does not add one —
 * so it is written down in the words, where a person can read it, rather than
 * invented as a field nothing else understands.
 *
 * ⛔ The AM's own words come FIRST and are never paraphrased. What we add is
 * appended and labelled, so nobody later mistakes our summary for their account.
 */
export function helperNote(a: HelperAnswersFull): string {
  const own = a.words.trim();
  const parts: string[] = [];

  const dir = moneyDirection(a.money);
  if (dir && a.money !== UNSURE) parts.push(`Money: ${dir.prompt.toLowerCase()}`);
  if (a.money === UNSURE) parts.push('Money: the AM was not sure — Finance to work it out');

  if (a.when === 'range' && a.date_from && a.date_to) {
    parts.push(`Over ${a.date_from} to ${a.date_to}`);
  }

  const gaps = unknownAnswers(a).filter((g) => g !== 'whether money needs to move');
  if (gaps.length > 0) parts.push(`Not sure about ${joinPlain(gaps)}`);

  const tail = parts.join('. ');
  if (!own) return tail ? `${tail}.` : '';
  return tail ? `${own}\n\n(${tail}.)` : own;
}
